/* Contractor Portal — register the truck IDs (fleet number + rego) the contractor owns. */
import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, CheckCircle2, CircleDashed, Factory } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-transparent border border-[#2C313A] text-white mono px-3 py-2 text-sm focus:outline-none focus:border-[#38BDF8]";
const EMPTY = { fleet_number: "", rego: "", capacity_m3: "", notes: "" };

export default function ContractorTrucks() {
  const [regs, setRegs] = useState([]);
  const [plants, setPlants] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [r, p] = await Promise.all([api.get("/contractor/trucks"), api.get("/contractor/plants").catch(() => ({ data: [] }))]);
    setRegs(r.data); setPlants(p.data);
  }, []);
  useEffect(() => { load().catch(() => toast.error("Could not load trucks")); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = { ...form, capacity_m3: form.capacity_m3 === "" ? null : Number(form.capacity_m3) };
      const r = await api.post("/contractor/trucks", payload);
      toast.success(r.data.matched ? `${form.rego.toUpperCase()} registered — matched ${r.data.matched.number} at ${r.data.matched.org_name}` : `${form.rego.toUpperCase()} registered — awaiting a plant match`);
      setForm(EMPTY); load();
    } catch (ex) { toast.error(formatApiErrorDetail(ex.response?.data?.detail)); } finally { setBusy(false); }
  };

  const del = async (r) => {
    if (!window.confirm(`Remove ${r.fleet_number} (${r.rego}) from your registered fleet?`)) return;
    try { await api.delete(`/contractor/trucks/${r.id}`); toast.success("Removed"); load(); } catch (ex) { toast.error(formatApiErrorDetail(ex.response?.data?.detail)); }
  };

  const matched = regs.filter((r) => r.matched).length;

  return (
    <div className="p-4 lg:p-8 space-y-6 max-w-5xl" data-testid="contractor-trucks-page">
      <div>
        <div className="text-[10px] tracking-[0.25em] text-[#8B909A]">FLEET REGISTER</div>
        <h1 className="heading text-2xl sm:text-3xl font-black uppercase text-white">My <span className="text-[#38BDF8]">Trucks</span></h1>
        <p className="text-sm text-[#A1A6B0] mt-2 max-w-2xl">Add each truck you own by its fleet number and registration plate. When a plant that has approved you dispatches a truck with the same rego, it appears on your Fleet Map automatically. Plants can also link a truck to you directly.</p>
      </div>

      <form onSubmit={add} className="bg-[#12151A] border border-[#22262E] p-4 grid grid-cols-2 md:grid-cols-[1fr_1fr_120px_1.5fr_auto] gap-3 items-end" data-testid="ctr-register-form">
        <div><label className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Fleet number</label><input data-testid="ctr-reg-fleet-number" required value={form.fleet_number} onChange={(e) => setForm({ ...form, fleet_number: e.target.value })} placeholder="DH-3" className={inputCls} /></div>
        <div><label className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Rego</label><input data-testid="ctr-reg-rego" required value={form.rego} onChange={(e) => setForm({ ...form, rego: e.target.value.toUpperCase() })} placeholder="ABC123" className={inputCls} /></div>
        <div><label className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Capacity m³</label><input data-testid="ctr-reg-capacity" type="number" step="0.5" min="0" value={form.capacity_m3} onChange={(e) => setForm({ ...form, capacity_m3: e.target.value })} placeholder="8" className={inputCls} /></div>
        <div><label className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Notes</label><input data-testid="ctr-reg-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" className={inputCls} /></div>
        <button data-testid="ctr-reg-submit" type="submit" disabled={busy} className="col-span-2 md:col-span-1 inline-flex items-center justify-center gap-1 bg-[#38BDF8] text-[#0B0D10] hover:bg-[#7DD3FC] font-black uppercase tracking-widest text-xs px-4 py-2.5 disabled:opacity-50"><Plus className="w-4 h-4" /> Add</button>
      </form>

      <div className="bg-[#12151A] border border-[#22262E]">
        <div className="px-4 py-3 border-b border-[#22262E] flex items-center justify-between text-[10px] tracking-widest uppercase text-[#8B909A]">
          <span>Registered trucks · {regs.length}</span><span data-testid="ctr-reg-matched-count">{matched} matched</span>
        </div>
        {regs.length === 0 && <div className="p-6 text-sm text-[#A1A6B0] text-center" data-testid="ctr-reg-empty">No trucks registered yet.</div>}
        <table className="w-full text-sm">
          <tbody>
            {regs.map((r) => (
              <tr key={r.id} className="border-b border-[#1B1F26] last:border-0" data-testid={`ctr-reg-row-${r.rego}`}>
                <td className="px-4 py-3 heading font-black text-white">{r.fleet_number}</td>
                <td className="px-4 py-3 mono text-[#E5E7EB]">{r.rego}</td>
                <td className="px-4 py-3 mono text-[#8B909A] hidden sm:table-cell">{r.capacity_m3 ? `${r.capacity_m3} m³` : "—"}</td>
                <td className="px-4 py-3">
                  {r.matched ? (
                    <span className="inline-flex items-center gap-1.5 text-emerald-400 text-xs" data-testid={`ctr-reg-status-${r.rego}`}><CheckCircle2 className="w-4 h-4" /> Matched · {r.matched.number} @ {r.matched.org_name}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[#8B909A] text-xs" data-testid={`ctr-reg-status-${r.rego}`}><CircleDashed className="w-4 h-4" /> Awaiting plant match</span>
                  )}
                </td>
                <td className="px-4 py-3 text-[#8B909A] text-xs hidden md:table-cell">{r.notes}</td>
                <td className="px-4 py-3 text-right"><button data-testid={`ctr-reg-delete-${r.rego}`} onClick={() => del(r)} className="text-[#6B717C] hover:text-red-400"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-[#12151A] border border-[#22262E] p-4" data-testid="ctr-approved-plants">
        <div className="text-[10px] tracking-widest uppercase text-[#8B909A] mb-2 flex items-center gap-1.5"><Factory className="w-3 h-3 text-[#F97316]" /> Plants that have approved your fleet</div>
        {plants.length === 0 && <div className="text-sm text-[#A1A6B0]">No plants yet — your plant contact adds you from their Fleet page.</div>}
        <div className="flex flex-wrap gap-2">
          {plants.map((o) => (
            <div key={o.org_id} className="border border-[#2C313A] px-3 py-2 text-sm" data-testid={`ctr-approved-org-${o.org_id}`}>
              <div className="font-bold text-white">{o.org_name}</div>
              <div className="text-[11px] text-[#8B909A]">{o.plants.map((p) => p.name).join(" · ") || "—"}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
